import React, { useRef } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { EmailBlock, EmailBlockType } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { RowActions } from './RowActions';

export const AvatarBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}> = ({ block, isSelected, onSelect, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  return (
    <Box sx={common} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onSelect}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Avatar : {block.title || 'Avatar'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
    </Box>
  );
};

export const AvatarBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  if (draft.type !== 'avatar') return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setDraft({ ...draft, imageUrl: result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setDraft({ ...draft, imageUrl: '' });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Avatar</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={draft.title || ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            fullWidth
            size="small"
          />

          <TextField
            label="Image URL"
            value={draft.url || ''}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            fullWidth
            size="small"
            placeholder="https://example.com/avatar.jpg"
          />

          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                onClick={handleFileButtonClick}
                fullWidth
              >
                Choose File
              </Button>
              {draft.imageUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveImage}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Remove
                </Button>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {draft.imageUrl && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={draft.imageUrl}
                  alt="Preview"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px', 
                    objectFit: 'contain',
                    borderRadius: draft.shape === 'circle' ? '50%' : draft.shape === 'rounded-square' ? '8px' : '0px',
                    width: draft.avatarSize ? `${draft.avatarSize}px` : 'auto',
                    height: draft.avatarSize ? `${draft.avatarSize}px` : 'auto'
                  }}
                />
              </Box>
            )}
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Align</InputLabel>
            <Select
              value={draft.align || 'center'}
              onChange={(e) => setDraft({ ...draft, align: e.target.value as any })}
              label="Align"
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Size (pixels)"
            type="number"
            value={draft.avatarSize || 64}
            onChange={(e) => setDraft({ ...draft, avatarSize: Number(e.target.value) })}
            fullWidth
            size="small"
            inputProps={{ min: 20, max: 200 }}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Shape</InputLabel>
            <Select
              value={draft.shape || 'circle'}
              onChange={(e) => setDraft({ ...draft, shape: e.target.value as any })}
              label="Shape"
            >
              <MenuItem value="circle">Circle</MenuItem>
              <MenuItem value="square">Square</MenuItem>
              <MenuItem value="rounded-square">Square with Round Edges</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

// Avatar block instantiation function
export const instantiateAvatarBlock = (): EmailBlock => {
  return {
    id: '',
    type: 'avatar',
    title: 'Avatar',
    url: 'https://via.placeholder.com/64x64',
    align: 'center',
    shape: 'circle',
    avatarSize: 64
  };
};

// Avatar block HTML rendering function
export const renderAvatarBlockToHtml = (block: EmailBlock): string => {
  const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  
  const align = block.align || 'center';
  const size = block.avatarSize || 64;
  const shape = block.shape || 'circle';
  const imageUrl = block.imageUrl || block.url || '';
  
  let borderRadius = '0px';
  if (shape === 'circle') {
    borderRadius = '50%';
  } else if (shape === 'rounded-square') {
    borderRadius = '8px';
  }
  
  return `<tr><td style="padding:16px 24px; text-align:${align}"><img src="${escapeHtml(imageUrl)}" style="width:${size}px; height:${size}px; border-radius:${borderRadius}; object-fit:cover;" /></td></tr>`;
};


